import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  SportsBasketball as BasketballIcon,
  CheckCircle as CheckCircleIcon,
  Undo as UndoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { usePlayer } from '../contexts/PlayerContext';
import { useTheme } from '@mui/material/styles';
import {
  playerService,
  FantasyLeague,
  Player,
  DraftPick,
  FantasyTeam,
  User,
} from '../services/playerService';

interface DraftPageProps {}

const DraftPage: React.FC<DraftPageProps> = () => {
  const { currentUsername, currentUser, setCurrentUser, isUserSwitching } = useUser();
  const { openPlayerDialog } = usePlayer();
  const theme = useTheme();

  const [leagues, setLeagues] = useState<FantasyLeague[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<FantasyLeague | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<any>(null);
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [draftPicks, setDraftPicks] = useState<DraftPick[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [initializingDraft, setInitializingDraft] = useState(false);
  const [makingPick, setMakingPick] = useState(false);
  const [userTeam, setUserTeam] = useState<FantasyTeam | null>(null);
  const [selectedTeamToView, setSelectedTeamToView] = useState<FantasyTeam | null>(null);
  const [undoingPick, setUndoingPick] = useState(false);
  const [resettingDraft, setResettingDraft] = useState(false);
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [dropAllPlayers, setDropAllPlayers] = useState(false);

  // Load user and leagues
  const loadUserData = useCallback(async () => {
    if (!currentUsername) return;

    try {
      setLoading(true);
      setError(null);

      const user = await playerService.getUserByUsername(currentUsername);
      setCurrentUser(user);

      const userLeagues = await playerService.getFantasyLeaguesByUser(user.id);
      setLeagues(userLeagues);

      // Auto-select first league if available
      if (userLeagues.length > 0 && !selectedLeague) {
        setSelectedLeague(userLeagues[0]);
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  }, [currentUsername, setCurrentUser, selectedLeague]);

  // Load draft data for selected league
  const loadDraftData = useCallback(async () => {
    if (!selectedLeague) return;

    try {
      setLoading(true);
      setError(null);

      // Get draft status
      const status = await playerService.getDraftStatus(selectedLeague.id);
      setDraftStatus(status);

      if (status.isInitialized && !status.isComplete) {
        // Load available players
        const players = await playerService.getDraftAvailablePlayers(selectedLeague.id);
        setAvailablePlayers(players);

        // Load all draft picks
        const picks = await playerService.getDraftPicks(selectedLeague.id);
        setDraftPicks(picks);

        // Find user's team in this league
        let foundUserTeam: FantasyTeam | null = null;
        if (currentUser) {
          const userTeams = await playerService.getFantasyTeamsByUser(currentUser.id);
          const team = userTeams.find((t) => t.leagueId === selectedLeague.id);
          foundUserTeam = team || null;
          setUserTeam(foundUserTeam);
        }
        
        // Auto-select team: default to user's team, or auto-switch when it's their turn
        const isUserTurn = status.currentPick && status.currentPick.ownerId === currentUser?.id;
        
        if (foundUserTeam) {
          // If it's the user's turn, always show their team
          if (isUserTurn) {
            setSelectedTeamToView(foundUserTeam);
          } else if (!selectedTeamToView) {
            // If no team is selected, default to user's team
            setSelectedTeamToView(foundUserTeam);
          }
        } else if (!selectedTeamToView && picks.length > 0) {
          // If no user team but we have picks, select the first team
          const firstTeam = picks[0]?.fantasyTeam;
          if (firstTeam) {
            setSelectedTeamToView(firstTeam);
          }
        }
      }
    } catch (err) {
      console.error('Error loading draft data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load draft data');
    } finally {
      setLoading(false);
    }
  }, [selectedLeague, currentUser]);

  // Initialize draft
  const handleInitializeDraft = async () => {
    if (!selectedLeague) return;

    try {
      setInitializingDraft(true);
      setError(null);

      await playerService.initializeDraft(selectedLeague.id, 8, dropAllPlayers);
      const message = dropAllPlayers 
        ? 'All players dropped from teams and draft initialized successfully!'
        : 'Draft initialized successfully!';
      setSnackbarMessage(message);
      setSnackbarOpen(true);
      setDropAllPlayers(false); // Reset checkbox after initialization
      await loadDraftData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize draft';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setInitializingDraft(false);
    }
  };

  // Make a draft pick
  const handleMakePick = async (player: Player) => {
    if (!draftStatus?.currentPick || !userTeam || makingPick) return;

    try {
      setMakingPick(true);
      setError(null);

      // Use the draftPickId from the status if available, otherwise find it
      const draftPickId = draftStatus.currentPick.draftPickId || draftPicks.find(
        (dp) =>
          dp.fantasyTeamId === draftStatus.currentPick.teamId &&
          dp.round === draftStatus.currentPick.round &&
          dp.pickNumber === draftStatus.currentPick.pickNumber &&
          !dp.playerId
      )?.id;

      if (!draftPickId) {
        throw new Error('Current draft pick not found. Please refresh the page.');
      }

      await playerService.makeDraftPick(draftPickId, player.id, userTeam.id);

      setSnackbarMessage(`${player.name} selected!`);
      setSnackbarOpen(true);

      // Refresh draft data after a short delay
      setTimeout(async () => {
        await loadDraftData();
      }, 500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to make draft pick';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setMakingPick(false);
    }
  };

  // Check if it's the user's turn
  const isUserTurn = () => {
    if (!draftStatus?.currentPick || !currentUser || !userTeam) return false;
    return draftStatus.currentPick.ownerId === currentUser.id;
  };

  // Undo last pick
  const handleUndoLastPick = async () => {
    if (!selectedLeague || undoingPick) return;

    try {
      setUndoingPick(true);
      setError(null);

      const result = await playerService.undoLastDraftPick(selectedLeague.id);
      setSnackbarMessage(`Undid pick #${result.pickNumber}: ${result.playerName}`);
      setSnackbarOpen(true);

      // Refresh draft data
      await loadDraftData();
      setUndoConfirmOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to undo last pick';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setUndoingPick(false);
    }
  };

  // Reset draft
  const handleResetDraft = async () => {
    if (!selectedLeague || resettingDraft) return;

    try {
      setResettingDraft(true);
      setError(null);

      const result = await playerService.resetDraft(selectedLeague.id);
      setSnackbarMessage(result.message);
      setSnackbarOpen(true);

      // Refresh draft data
      await loadDraftData();
      setResetConfirmOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset draft';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setResettingDraft(false);
    }
  };

  // Check if there are any picks to undo
  const hasPicksToUndo = draftStatus?.completedPicksCount && draftStatus.completedPicksCount > 0;

  // Filter players by search term
  const filteredPlayers = availablePlayers.filter(
    (player) =>
      player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.team.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load data on mount
  useEffect(() => {
    if (currentUsername && !isUserSwitching) {
      loadUserData();
    }
  }, [currentUsername, isUserSwitching, loadUserData]);

  // Reset selected team when league changes
  useEffect(() => {
    setSelectedTeamToView(null);
  }, [selectedLeague?.id]);

  // Load draft data when league changes
  useEffect(() => {
    if (selectedLeague) {
      loadDraftData();
      // Set up polling to refresh draft status every 5 seconds
      const interval = setInterval(() => {
        loadDraftData();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [selectedLeague, loadDraftData]);

  if (isUserSwitching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Switching to {currentUsername}...
        </Typography>
      </Box>
    );
  }

  if (loading && !draftStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !draftStatus) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        <BasketballIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Draft
      </Typography>

      {/* League Selection */}
      {leagues.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <FormControl fullWidth>
              <InputLabel>Select League</InputLabel>
              <Select
                value={selectedLeague?.id || ''}
                label="Select League"
                onChange={(e) => {
                  const league = leagues.find((l) => l.id === e.target.value);
                  setSelectedLeague(league || null);
                }}
              >
                {leagues.map((league) => (
                  <MenuItem key={league.id} value={league.id}>
                    {league.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {selectedLeague && (
        <>
          {/* Draft Status Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              {!draftStatus?.isInitialized ? (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Draft Not Initialized
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Initialize the draft to begin. This will create 8 rounds of picks for all 10 teams.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dropAllPlayers}
                        onChange={(e) => setDropAllPlayers(e.target.checked)}
                        disabled={initializingDraft}
                      />
                    }
                    label="Drop all players from teams before initializing"
                    sx={{ mb: 2, display: 'block' }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleInitializeDraft}
                    disabled={initializingDraft}
                  >
                    {initializingDraft ? <CircularProgress size={24} /> : 'Initialize Draft'}
                  </Button>
                </Box>
              ) : draftStatus.isComplete ? (
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
                    <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Draft Complete!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    All {draftStatus.totalPicks} picks have been made.
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You can restart the draft to begin again. This will reset all draft picks and optionally remove all players from teams.
                  </Alert>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dropAllPlayers}
                        onChange={(e) => setDropAllPlayers(e.target.checked)}
                        disabled={initializingDraft}
                      />
                    }
                    label="Drop all players from teams before restarting"
                    sx={{ mb: 2, display: 'block' }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleInitializeDraft}
                    disabled={initializingDraft}
                    startIcon={initializingDraft ? <CircularProgress size={20} /> : <UndoIcon />}
                  >
                    {initializingDraft ? 'Restarting Draft...' : 'Restart Draft'}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Round
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {draftStatus.currentRound} / {draftStatus.totalRounds}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Pick Number
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {draftStatus.currentPick?.pickNumber} / {draftStatus.totalPicks}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        On The Clock
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary">
                        {draftStatus.currentPick?.teamName || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {draftStatus.currentPick?.ownerName}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Progress: {draftStatus.completedPicksCount} / {draftStatus.totalPicks} picks completed (
                      {Math.round((draftStatus.completedPicksCount / draftStatus.totalPicks) * 100)}%)
                    </Typography>
                  </Box>
                  {isUserTurn() && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      It's your turn to pick!
                    </Alert>
                  )}
                  {draftStatus.isInitialized && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {hasPicksToUndo && (
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={<UndoIcon />}
                          onClick={() => setUndoConfirmOpen(true)}
                          disabled={undoingPick}
                        >
                          Undo Last Pick
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setResetConfirmOpen(true)}
                        disabled={resettingDraft}
                      >
                        Reset Draft
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {draftStatus?.isInitialized && !draftStatus.isComplete && (
            <Grid container spacing={3}>
              {/* Team Rosters */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Team Roster
                    </Typography>
                    {draftPicks.length > 0 ? (() => {
                      // Get all unique teams from draft picks
                      const picksByTeam = draftPicks.reduce((acc, pick) => {
                        const teamId = pick.fantasyTeamId;
                        if (!acc[teamId]) {
                          acc[teamId] = {
                            team: pick.fantasyTeam,
                            picks: []
                          };
                        }
                        // Only add picks that have a player assigned
                        if (pick.playerId && pick.player) {
                          acc[teamId].picks.push(pick);
                        }
                        return acc;
                      }, {} as Record<number, { team: FantasyTeam; picks: DraftPick[] }>);

                      const allTeams = Object.values(picksByTeam)
                        .map(({ team }) => team)
                        .sort((a, b) => a.name.localeCompare(b.name));

                      // Get the selected team's picks
                      const selectedTeamData = selectedTeamToView 
                        ? picksByTeam[selectedTeamToView.id]
                        : null;

                      const picks = selectedTeamData?.picks || [];
                      const team = selectedTeamToView;
                      const isCurrentTeam = team?.id === draftStatus.currentPick?.teamId;
                      const isUserTeam = team?.id === userTeam?.id;

                      return (
                        <Box>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>View Team</InputLabel>
                            <Select
                              value={selectedTeamToView?.id || ''}
                              label="View Team"
                              onChange={(e) => {
                                const teamId = e.target.value as number;
                                const team = allTeams.find((t) => t.id === teamId);
                                setSelectedTeamToView(team || null);
                              }}
                            >
                              {allTeams.map((teamOption) => (
                                <MenuItem key={teamOption.id} value={teamOption.id}>
                                  {teamOption.name}
                                  {teamOption.id === userTeam?.id && ' (You)'}
                                  {teamOption.id === draftStatus.currentPick?.teamId && ' ⏱️'}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {team ? (
                            <Box>
                              <Box sx={{ 
                                mb: 2, 
                                p: 2, 
                                borderRadius: 1,
                                bgcolor: isCurrentTeam ? 'primary.light' : isUserTeam ? 'action.selected' : 'transparent',
                                border: isCurrentTeam ? '2px solid' : '1px solid',
                                borderColor: isCurrentTeam ? 'primary.main' : 'divider',
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {team.name}
                                    {isCurrentTeam && ' ⏱️'}
                                    {isUserTeam && ' (You)'}
                                  </Typography>
                                  <Chip 
                                    label={`${picks.length}/8`} 
                                    size="small" 
                                    color={isCurrentTeam ? 'primary' : isUserTeam ? 'primary' : 'default'}
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {team.owner?.username || 'Unknown'}
                                </Typography>
                              </Box>

                              <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                                {picks.length > 0 ? (
                                  <List dense>
                                    {picks
                                      .sort((a, b) => a.pickNumber - b.pickNumber)
                                      .map((pick) => (
                                        <ListItem
                                          key={pick.id}
                                          sx={{
                                            px: 0,
                                            py: 0.5,
                                            mb: 0.5,
                                            '&:hover': {
                                              bgcolor: 'action.hover',
                                              borderRadius: 1,
                                            },
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  cursor: 'pointer',
                                                  '&:hover': { textDecoration: 'underline' },
                                                }}
                                                onClick={() => pick.player && openPlayerDialog(pick.player)}
                                              >
                                                {pick.player?.name || 'N/A'}
                                              </Typography>
                                            }
                                            secondary={
                                              <Typography variant="caption" color="text.secondary">
                                                Round {pick.round} • Pick {pick.pickNumber} • {pick.player?.fantasyPointsPerGame.toFixed(1)} FP/G
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      ))}
                                  </List>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                    No picks yet
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Select a team to view
                            </Typography>
                          )}
                        </Box>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">
                        No teams found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Available Players */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Available Players
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Search players..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Player</TableCell>
                            <TableCell>Team</TableCell>
                            <TableCell>Position</TableCell>
                            <TableCell align="right">Avg FP</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredPlayers.map((player) => (
                            <TableRow key={player.id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: 'primary.main',
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    {player.name
                                      .split(' ')
                                      .map((n) => n[0])
                                      .join('')
                                      .toUpperCase()
                                      .slice(0, 2)}
                                  </Avatar>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                    onClick={() => openPlayerDialog(player)}
                                  >
                                    {player.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{player.team}</TableCell>
                              <TableCell>{player.position}</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="bold">
                                  {player.fantasyPointsPerGame.toFixed(1)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="primary"
                                  disabled={!isUserTurn() || makingPick}
                                  onClick={() => handleMakePick(player)}
                                >
                                  {makingPick ? <CircularProgress size={20} /> : 'Draft'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {filteredPlayers.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No players found
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Draft Board */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Draft Board
                    </Typography>
                    <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                      {draftPicks.length > 0 ? (
                        <Box>
                          {Array.from({ length: draftStatus.totalRounds || 8 }, (_, roundIndex) => {
                            const round = roundIndex + 1;
                            const roundPicks = draftPicks.filter((dp) => dp.round === round);
                            return (
                              <Box key={round} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                  Round {round}
                                </Typography>
                                <List dense>
                                  {roundPicks.map((pick) => (
                                    <ListItem
                                      key={pick.id}
                                      sx={{
                                        bgcolor: pick.playerId
                                          ? 'action.selected'
                                          : pick.pickNumber === draftStatus.currentPick?.pickNumber
                                          ? 'primary.light'
                                          : 'transparent',
                                        borderRadius: 1,
                                        mb: 0.5,
                                      }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2">
                                              {pick.pickNumber}. {pick.fantasyTeam.name}
                                            </Typography>
                                            {pick.playerId && (
                                              <Chip
                                                label={pick.player?.name || 'N/A'}
                                                size="small"
                                                color="primary"
                                              />
                                            )}
                                          </Box>
                                        }
                                        secondary={
                                          pick.playerId ? (
                                            <Typography variant="caption" color="text.secondary">
                                              {pick.player?.fantasyPointsPerGame.toFixed(1)} FP/G
                                            </Typography>
                                          ) : (
                                            <Typography variant="caption" color="text.secondary">
                                              Pending...
                                            </Typography>
                                          )
                                        }
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No picks yet
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Undo Confirmation Dialog */}
      <Dialog open={undoConfirmOpen} onClose={() => setUndoConfirmOpen(false)}>
        <DialogTitle>Undo Last Pick?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to undo the last draft pick? This will remove the player from the team
            and make them available again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUndoLastPick}
            color="warning"
            variant="contained"
            disabled={undoingPick}
            startIcon={undoingPick ? <CircularProgress size={20} /> : <UndoIcon />}
          >
            {undoingPick ? 'Undoing...' : 'Undo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Draft Confirmation Dialog */}
      <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)}>
        <DialogTitle>Reset Draft?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to completely reset the draft? This will:
          </Typography>
          <Typography component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Remove all draft picks</li>
            <li>Remove all players from teams (acquired via draft)</li>
            <li>Reset the draft back to uninitialized state</li>
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            You will need to re-initialize the draft to start again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResetDraft}
            color="error"
            variant="contained"
            disabled={resettingDraft}
            startIcon={resettingDraft ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {resettingDraft ? 'Resetting...' : 'Reset Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DraftPage;
